import React, { useState, useEffect, useRef } from 'react';
import { Input } from 'antd';
import { formatDateInput, validateAndLimitDate } from '../../utils/dateUtils';

/**
 * 统一日期输入组件
 *
 * 合并 DepartmentView（鼠标滚轮、选择跟踪）和 EditableDataTable（autoFocus、键盘事件）的功能
 *
 * @param {Object} props
 * @param {string} props.value - 当前日期值（YYYY-MM-DD）
 * @param {function} props.onChange - 值变更回调
 * @param {function} [props.onBlur] - 失焦回调
 * @param {function} [props.onPressEnter] - 回车回调
 * @param {function} [props.onKeyDown] - 键盘事件回调
 * @param {string} [props.placeholder] - 占位文本
 * @param {Object} [props.style] - 样式
 * @param {boolean} [props.autoFocus=false] - 自动聚焦
 * @param {string} [props.data-cell-key] - 表格内编辑标识
 * @param {boolean} [props.enableWheel=false] - 启用滚轮调节日期
 */
const DateInput = ({
  value,
  onChange,
  onBlur,
  onPressEnter,
  onKeyDown,
  placeholder,
  style,
  autoFocus = false,
  'data-cell-key': dataCellKey,
  enableWheel = false,
}) => {
  const inputRef = useRef(null);
  const isSelectingRef = useRef(false);
  const [localValue, setLocalValue] = useState(value || '');

  useEffect(() => {
    setLocalValue(value || '');
  }, [value]);

  useEffect(() => {
    if (autoFocus && inputRef.current) {
      inputRef.current.focus();
    }
  }, [autoFocus]);

  const handleChange = (e) => {
    const formatted = formatDateInput(e.target.value);
    setLocalValue(formatted);
    onChange(formatted);
  };

  const validateAndFormat = () => {
    const parts = localValue.split('-');
    if (parts.length === 3) {
      const validated = validateAndLimitDate(parts[0], parts[1], parts[2]);
      const formatted = `${validated.year}-${validated.month}-${validated.day}`;
      setLocalValue(formatted);
      onChange(formatted);
      return formatted;
    }
    return localValue;
  };

  const handleBlur = (e) => {
    if (isSelectingRef.current) {
      isSelectingRef.current = false;
      if (inputRef.current) inputRef.current.focus();
      return;
    }
    validateAndFormat();
    if (onBlur) onBlur(e);
  };

  const handlePressEnter = (e) => {
    validateAndFormat();
    if (onPressEnter) onPressEnter(e);
  };

  // 滚轮调节日期（用于 DepartmentView 场景）
  const handleWheel = (e) => {
    if (!enableWheel) return;
    e.preventDefault();
    const parts = localValue.split('-');
    let year = parseInt(parts[0]) || 0;
    let month = parseInt(parts[1]) || 1;
    let day = parseInt(parts[2]) || 1;

    const cursorPos = inputRef.current?.input?.selectionStart || 0;
    if (cursorPos <= 4) {
      year += e.deltaY > 0 ? -1 : 1;
      year = Math.min(Math.max(year, 1900), 2100);
    } else if (cursorPos <= 7) {
      month += e.deltaY > 0 ? -1 : 1;
      if (month < 1) { month = 12; year = Math.max(year - 1, 1900); }
      if (month > 12) { month = 1; year = Math.min(year + 1, 2100); }
    } else {
      day += e.deltaY > 0 ? -1 : 1;
      const maxDay = new Date(year, month, 0).getDate();
      if (day < 1) { day = maxDay; month -= 1; if (month < 1) { month = 12; year -= 1; } }
      if (day > maxDay) { day = 1; month += 1; if (month > 12) { month = 1; year += 1; } }
    }

    const validated = validateAndLimitDate(year, month, day);
    const formatted = `${validated.year}-${validated.month}-${validated.day}`;
    setLocalValue(formatted);
    onChange(formatted);
  };

  return (
    <Input
      ref={inputRef}
      value={localValue}
      onChange={handleChange}
      onBlur={handleBlur}
      onPressEnter={handlePressEnter}
      onKeyDown={onKeyDown}
      onWheel={enableWheel ? handleWheel : undefined}
      style={style}
      placeholder={placeholder}
      maxLength={10}
      data-cell-key={dataCellKey}
      autoFocus={autoFocus}
      allowClear={!enableWheel}
    />
  );
};

export default DateInput;
